import { Toast } from '@byecode/ui'
import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { ChartBlock } from '@lighthouse/block/src/blocks'
import { type ChartBlockAbstract, EVENT_TYPE } from '@lighthouse/core'
import {
    chartProtocolsDataKey,
    dataDrawerPubSub,
    getWithScopeIdAndRecordIdAndColumnId,
    pageStackPubSub,
    useActionRunningLoadings,
    useAtomAsyncAction,
    useAtomData,
    useHandleAbortPrevious
} from '@lighthouse/shared'
import { deepOmitNil } from '@lighthouse/tools'
import fastEqual from 'fast-deep-equal'
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import { useMount, useUpdateEffect } from 'react-use'

import { fetchChartBlockAtom } from '@/atoms/blockRecordsDict/action'
import { blockCreatingListAtom, blockUpdateListAtom } from '@/atoms/page/state'
import { useCurrentPageContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useFilter } from '@/hooks/useFilter'
import { useLinkFilter } from '@/hooks/useLinkFilter'
import * as srv from '@/services'

interface GalleryBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: ChartBlockAbstract
}

const ChartBlockController: React.FC<GalleryBlockControllerProps> = ({ blockData, ...rest }) => {
    const { id, config, title } = blockData
    const appId = useCurrentAppId()
    const location = useLocation()
    const { pageId } = useCurrentPageContext()
    const envId = useCurrentEnvId()
    const { scope } = useContainerBlockContext()
    const dataSourceList = useDataSourceList(appId, envId)
    const { pointer, linkFilterController, ruleFilter, eventList } = config
    const { record } = useCustomViewBlockContext()
    const { handleActionListTrigger } = useActionTrigger(id)
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const hasNotCreatedYet = useAtomData(
        blockCreatingListAtom,
        useCallback(s => s.includes(id), [id])
    )

    const hasNeedUpdateYet = useAtomData(
        blockUpdateListAtom,
        useCallback(s => s.includes(id), [id])
    )

    const { run: fetchChartBlock, loading, value: chartData } = useAtomAsyncAction(fetchChartBlockAtom)
    const { mutation } = useHandleAbortPrevious(fetchChartBlock)
    const isFetch = useMemo(() => {
        return !!pointer
    }, [pointer])

    const includeDataConfig = useMemo(() => {
        const { ruleFilter, linkFilterController, ...rest } = config
        const dataConfig = Object.fromEntries(Object.entries(rest).filter(([key]) => chartProtocolsDataKey.has(key)))
        return deepOmitNil(dataConfig)
    }, [config])

    const { resolvedFilter } = useFilter({
        filter: ruleFilter,
        dataSourceList
    })

    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    const configRef = useRef({
        resolvedLinkFilter,
        resolvedFilter,
        includeDataConfig
    })

    const handleExport = useCallback(
        async (ev: React.MouseEvent) => {
            ev.stopPropagation()
            Toast.warning('正在导出, 请勿重复点击')
            const res = await srv.exportChart({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter
            })
            const { data, headers } = res
            const fileName = headers?.['content-disposition']?.replace?.("attachment;filename*=utf-8''", '') ?? `${title}.xlsx`
            const dom = document.createElement('a')
            const url = window.URL.createObjectURL(data)
            dom.href = url
            dom.download = decodeURI(fileName)
            dom.style.display = 'none'
            document.body.append(dom)
            dom.click()
            dom.remove()
            window.URL.revokeObjectURL(url)
        },
        [id, scope, resolvedLinkFilter, title]
    )

    useMount(() => {
        if (isFetch) {
            mutation({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter,
                ruleFilter: resolvedFilter
            })
        }
    })

    useEffect(() => {
        if (!pointer) {
            return
        }
        // 订阅页面栈对记录的操作
        const { subscribeId: addSubscribeId, unSubscribe: unAddSubscribe } = pageStackPubSub.subscribe(`${pointer}-ADD`, () => {
            mutation({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter,
                ruleFilter: resolvedFilter
            })
        })
        // 订阅数据查看器对记录的操作
        const { subscribeId: deleteSubscribeId, unSubscribe: unDeleteSubscribe } = dataDrawerPubSub.subscribe(`${pointer}-DELETE`, () => {
            mutation({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter,
                ruleFilter: resolvedFilter
            })
        })
        return () => {
            unAddSubscribe(addSubscribeId)
            unDeleteSubscribe(deleteSubscribeId)
        }
    }, [mutation, id, pointer, resolvedLinkFilter, resolvedFilter, scope])

    // 监听filter
    useUpdateEffect(() => {
        if (
            fastEqual(configRef.current.resolvedLinkFilter, resolvedLinkFilter) &&
            fastEqual(configRef.current.resolvedFilter, resolvedFilter)
        ) {
            return
        }
        configRef.current.resolvedLinkFilter = resolvedLinkFilter
        configRef.current.resolvedFilter = resolvedFilter
        mutation({
            blockId: id,
            isSyncComponent: !!scope,
            linkFilter: resolvedLinkFilter,
            ruleFilter: resolvedFilter
        })
    }, [ruleFilter, resolvedLinkFilter, resolvedFilter])

    // 监听配置
    useUpdateEffect(() => {
        if (fastEqual(configRef.current.includeDataConfig, includeDataConfig)) {
            return
        }
        configRef.current.includeDataConfig = includeDataConfig
        mutation({ blockId: id, isSyncComponent: !!scope, linkFilter: resolvedLinkFilter, ruleFilter: resolvedFilter })
    }, [includeDataConfig])

    // 当数据查看器进行增删改时 需要重载
    useUpdateEffect(() => {
        if (hasNeedUpdateYet && isFetch) {
            mutation({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter,
                ruleFilter: resolvedFilter
            })
        }
    }, [hasNeedUpdateYet])

    useUpdateEffect(() => {
        if (hasNotCreatedYet || location.pathname.includes('/dataSource/') || location.pathname.includes('/flow')) {
            return
        }
        if (isFetch) {
            mutation({
                blockId: id,
                isSyncComponent: !!scope,
                linkFilter: resolvedLinkFilter,
                ruleFilter: resolvedFilter
            })
        }
    }, [mutation, id, scope, isFetch, hasNotCreatedYet, location.pathname, hasNeedUpdateYet])

    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])

    const handleClickEvent = useCallback(() => {
        if (!clickEvent || loadings[uniqueId]) {
            return
        }
        return handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent: clickEvent,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
        })
    }, [clickEvent, handleActionListTrigger, handleActionTriggerWithLoading, loadings, record, uniqueId])

    return (
        <ChartBlock
            loading={loading}
            blockData={blockData}
            chartData={chartData}
            onExport={handleExport}
            onClick={handleClickEvent}
            {...rest}
            data-stop-action-propagation={clickEvent ? true : undefined}
        />
    )
}

export default memo(ChartBlockController, fastEqual)
